import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api/rest";
import type { Market } from "../api/types";
import { useNow } from "../hooks/useNow";
import { shortId } from "../lib/format";
import { trackingNotice } from "../lib/tracking";
import { valuationPollMs } from "../lib/valuation";
import { Field, FieldRow, Val, W } from "./Field";
import { IndexCheck } from "./IndexCheck";
import { IndexRow } from "./IndexRow";
import { SeriesLinks, SeriesNav } from "./SeriesNav";

/**
 * Карточка текущего рынка — ВСЁ про идущее окно в одном месте.
 *
 * Было тремя блоками на разной высоте: шапка с окном и отсчётом, строка индекса
 * под ней и карточка «Сверка индекса» в правой колонке, ниже панели быстрых
 * действий. Числа в них об одном и том же — где страйк, где индекс, сколько до
 * конца окна и можно ли ещё перевернуть исход, — но читать их приходилось в двух
 * концах экрана, а прокрутка между ними стоила тех самых секунд, ради которых
 * пятиминутное окно и торгуют.
 *
 * Три яруса, сверху вниз, от решения к его обоснованию:
 *
 *   1. окно, отсчёт и шаг цены — что торгуем;
 *   2. страйк, индекс, отклонение и оценка модели — на чём решение;
 *   3. сверка того же индекса и замок исхода — можно ли этим числам верить и
 *      осталось ли чему меняться.
 *
 * # Что уехало в КОНЕЦ первой строки и почему
 *
 * Соседние окна и закрытие рынка. Оба нужны раз в пять минут — когда окно
 * кончилось, — а стояли между «что торгую» и «шаг цены», то есть клином посреди
 * читаемого каждую секунду. Закрытие вдобавок ужато до крестика: строка ЛИПКАЯ,
 * и «← к списку рынков» оплачивалось высотой экрана постоянно.
 *
 * Минимальный размер уехал в подсказку по тому же счёту: он от рынка к рынку не
 * меняется так, чтобы на него смотрели, а отказ формы называет величину сам.
 *
 * Оценку карточка спрашивает ОДНИМ запросом на все ярусы: строка индекса и
 * сверка под ней описывают одно и то же значение, и разъехаться по возрасту они
 * не вправе. Раньше их спасал общий ключ react-query, теперь спасать нечего.
 *
 * Что уехало в подсказку контейнера: полный вопрос рынка и condition_id. Вопрос
 * дублирует разобранное окно («Bitcoin Up or Down - August 9, 5:50PM-5:55PM ET»
 * против «BTC · 5m · 16:50–16:55»), а второе короче и в местном часовом поясе;
 * condition_id нужен при разборе инцидента, а не в торговле.
 */
export function MarketOverview({
  market,
  conditionID,
  markets,
  fetchedAt,
  books,
  onToggleBooks,
}: {
  market: Market | undefined;
  conditionID: string;
  markets: readonly Market[];
  /** Когда флаг подписки читался из справочника — для пометки о подписке. */
  fetchedAt: number;
  books: boolean;
  onToggleBooks: () => void;
}) {
  /**
   * Опрос раз в секунду — ПОКА ОКНО ИДЁТ.
   *
   * Не сокет: индекс — это состояние («сколько стоит сейчас»), а не поток, где
   * важен порядок, и заводить ради него новый тип сообщения значило бы трогать
   * контракт сокета. Секунда совпадает с каденцией источника: чаще спрашивать
   * попросту нечего.
   *
   * У закончившегося окна такт другой и в конце концов гасится вовсе: карточка
   * показывает итог окна, а он не меняется. Правило — в lib/valuation.ts, потому
   * что доезжает правда двумя частями и не мгновенно (итог метрики через
   * полторы секунды, исход до двух минут).
   */
  const q = useQuery({
    queryKey: ["valuation", conditionID],
    queryFn: () => api.valuation(conditionID).then((r) => r.valuation),
    enabled: Boolean(conditionID),
    refetchInterval: (query) => valuationPollMs(query.state.data),
  });

  const fallback = market?.question || market?.slug || shortId(conditionID, 20);

  return (
    <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)]">
      <div
        className="border-b border-[var(--color-border)] px-4 py-1 text-xs"
        title={`${market?.question ?? ""}\n${conditionID}`}
      >
        <FieldRow>
        {market ? (
          <SeriesNav market={market} markets={markets} fallback={fallback} />
        ) : (
          <span className="font-medium">{fallback}</span>
        )}

        {/* Шаг цены объясняет отказы формы и выключенные чипы долей — без него
            оператор ищет причину в интерфейсе.

            Минимальный размер уехал в подсказку: он не меняется от рынка к
            рынку так, чтобы на него смотрели, а место в липкой строке стоит
            дорого. Отказ формы по нему называет величину сам. */}
        <Field
          label="шаг"
          title={`минимальный размер ордера: ${market?.min_order_size ?? "—"}`}
        >
          <Val w={W.tick}>{market?.tick_size ?? "—"}</Val>
        </Field>
        {/* neg risk — ТОЛЬКО когда он есть: это исключение, и строка «нет» на
            каждом рынке приучает не читать это место вовсе. */}
        {market?.neg_risk && (
          <span className="text-amber-300" title="рынок в связке neg risk">
            neg risk
          </span>
        )}
        {market && <TrackingBadge market={market} fetchedAt={fetchedAt} />}

        {/* Переключатель стаканов — здесь, а не над сеткой: карточка липкая, и
            позвать лесенки оператор должен из любого места прокрутки.
            Треугольник тот же, что у свёрнутых держателей в панели участников. */}
        <button
          onClick={onToggleBooks}
          className="ml-auto text-[var(--color-muted)] hover:text-white"
          title={
            "лесенки стаканов Up и Down. Свёрнуты по умолчанию: место занимает " +
            "накопление участников. Данные стакана идут независимо от показа — " +
            "панель быстрых действий и клавиши работают в обоих состояниях"
          }
        >
          {books ? "▾" : "▸"} стаканы
        </button>

        {/* Соседние окна — В КОНЦЕ строки, рядом с закрытием рынка.
            Смотрят на них раз в пять минут, когда окно кончилось, а между
            «что торгую» и «шаг цены» они стояли клином каждую секунду. */}
        {market && <SeriesLinks market={market} markets={markets} />}

        {/* Крестик, а не «← к списку рынков»: строка ЛИПКАЯ, и четырнадцать
            знаков в ней оплачиваются высотой экрана постоянно, а нажимают на
            них раз в сеанс. Смысл остаётся в подсказке. */}
        <Link
          to="/"
          className="text-[var(--color-muted)] hover:text-white"
          title="закрыть рынок и вернуться к списку"
          aria-label="закрыть рынок и вернуться к списку"
        >
          ✕
        </Link>
        </FieldRow>
      </div>

      <IndexRow
        v={q.data}
        fees={market?.fees}
        tokens={market?.tokens}
        note={q.isError ? `индекс: ${(q.error as Error).message}` : "индекс: загрузка…"}
      />
      <IndexCheck v={q.data} />
    </section>
  );
}

/**
 * Пометка о подписке трейдера. Лист с собственным тиком часов (правило
 * hooks/useNow.ts): фаза окна меняется по времени, и без тика «подписка
 * появится к открытию» висела бы рядом с уже идущим отсчётом до следующей
 * перерисовки родителя.
 *
 * Жёлтая тревога — только когда стакана нет там, где он обязан быть; штатные
 * состояния роловера показываются приглушённой справкой. Разбор состояний — в
 * lib/tracking.ts.
 */
function TrackingBadge({ market, fetchedAt }: { market: Market; fetchedAt: number }) {
  const nowMs = useNow(1000);
  const notice = trackingNotice(market.slug, market.tracked, new Date(nowMs), fetchedAt);
  if (!notice) return null;
  return (
    <span
      className={notice.tone === "alarm" ? "text-amber-300" : "text-[var(--color-muted)]"}
      title={notice.title}
    >
      {notice.text}
    </span>
  );
}
